/**
 * @class ChangeDetector
 * @description 
 * This class uses deep cloning and comparison to track changes in variables over time.
 * It supports both direct value registration and getter function registration for
 * real-time change detection.
 * 
 * @example
 * // Basic usage with primitive values
 * const detector = new ChangeDetector();
 * let userName = 'John';
 * let userAge = 25;
 * 
 * detector.registerVariablesWithGetters({
 *   'name': () => userName,
 *   'age': () => userAge
 * });
 */
export class ChangeDetector {
  private originalValues: Map<string, any> = new Map();
  private trackedVariables: Map<string, () => any> = new Map();

  /**
   * Register variables to track for changes using direct values.
   * 
   * @method registerVariables
   * @param {Object.<string, any>} variables - Object with key-value pairs where key is identifier and value is the variable to track
   * @throws {Error} Throws error if variables parameter is not an object
   * 
   * @description
   * This method registers variables by capturing their current values at registration time.
   * Note: This approach may not detect changes if you're passing object references that get
   * modified later. Use registerVariablesWithGetters() for better change detection.
   * 
   * @example
   * // Simple primitive registration
   * const detector = new ChangeDetector();
   * const userName = 'John';
   * const userAge = 25;
   * 
   * detector.registerVariables({
   *   'name': userName,
   *   'age': userAge
   * });
   */
  registerVariables(variables: { [key: string]: any }): void {
    Object.entries(variables).forEach(([key, value]) => {
      console.log("Key: ", key, " , Value: ", value)

      this.originalValues.set(key, this.deepClone(value));
      // Store a function that returns the current value
      this.trackedVariables.set(key, () => value);
    });
  }

  /**
   * Register variables with getter functions to track for changes (Recommended approach).
   * 
   * @method registerVariablesWithGetters
   * @param {Object.<string, () => any>} variables - Object with key-function pairs where function returns current value
   * @throws {Error} Throws error if any getter is not a function
   * 
   * @description
   * This is the preferred method for registering variables as it uses getter functions
   * to always access the current state of variables, ensuring accurate change detection
   * even for complex nested objects and arrays.
   * 
   * @example
   * // Component property tracking
   * class MyComponent {
   *   private detector = new ChangeDetector();
   *   user = { name: 'John', profile: { bio: 'Developer' } };
   *   items = ['item1', 'item2'];
   *   
   *   ngOnInit() {
   *     this.detector.registerVariablesWithGetters({
   *       'user': () => this.user,
   *       'userProfile': () => this.user.profile,
   *       'items': () => this.items,
   *       'itemCount': () => this.items.length
   *     });
   *   }
   * }
   */
  registerVariablesWithGetters(variables: { [key: string]: () => any }): void {
    Object.entries(variables).forEach(([key, getter]) => {
      this.originalValues.set(key, this.deepClone(getter()));
      this.trackedVariables.set(key, getter);
    });
  }

  /**
   * Register Angular FormGroups and FormControls for change tracking.
   * 
   * @method registerFormGroups
   * @param {Object.<string, AbstractControl>} forms - Object with key-FormControl/FormGroup pairs
   * @throws {Error} Throws error if any form control is null or undefined
   * 
   * @description
   * Specialized method for tracking Angular Reactive Forms. This method automatically
   * registers FormGroups, FormControls, and FormArrays by tracking their values and
   * validation states. It handles nested form structures and provides comprehensive
   * change detection for form data, validation states, and form metadata.
   * 
   * **Tracked Properties for each Form:**
   * - **Value**: The current form value (form.value)
   * - **Raw Value**: The raw form value including disabled controls (form.getRawValue())
   * - **Valid State**: Form validation status (form.valid)
   * - **Errors**: Validation errors (form.errors)
   * - **Touched State**: Whether form has been touched (form.touched)
   * - **Dirty State**: Whether form has been modified (form.dirty)
   * - **Status**: Form status (VALID, INVALID, PENDING, DISABLED)
   * 
   * @example
   * // Basic FormGroup registration
   * @Component({
   *   template: `
   *     <form [formGroup]="userForm">
   *       <input formControlName="name" placeholder="Name">
   *       <input formControlName="email" placeholder="Email">
   *       <input formControlName="age" type="number" placeholder="Age">
   *       
   *       <div *ngIf="detector.isVariableDirty('userForm_value')" class="unsaved-changes">
   *         Unsaved changes detected!
   *       </div>
   *     </form>
   *   `
   * })
   * class FormComponent implements OnInit {
   *   private detector = new ChangeDetector();
   *   
   *   userForm = this.fb.group({
   *     name: ['John Doe', Validators.required],
   *     email: ['john@example.com', [Validators.required, Validators.email]],
   *     age: [30, [Validators.required, Validators.min(18)]]
   *   });
   *   
   *   ngOnInit() {
   *     // Register the FormGroup for comprehensive tracking
   *     this.detector.registerFormGroups({
   *       'userForm': this.userForm
   *     });
   *   }
   *   
   *   get hasFormChanges(): boolean {
   *     return this.detector.isVariableDirty('userForm_value');
   *   }
   *   
   *   get hasValidationChanges(): boolean {
   *     return this.detector.isVariableDirty('userForm_errors');
   *   }
   * }
   */
  registerFormGroups(forms: { [key: string]: any }): void {
    Object.entries(forms).forEach(([formName, formControl]) => {
      if (!formControl) {
        throw new Error(`Form control '${formName}' is null or undefined`);
      }

      // Register core form properties with getters
      const formGetters: { [key: string]: () => any } = {
        // Value tracking
        [`${formName}_value`]: () => formControl.value,
        [`${formName}_rawValue`]: () => formControl.getRawValue ? formControl.getRawValue() : formControl.value,

        // Validation tracking
        [`${formName}_valid`]: () => formControl.valid,
        [`${formName}_invalid`]: () => formControl.invalid,
        [`${formName}_errors`]: () => formControl.errors,

        // State tracking
        [`${formName}_touched`]: () => formControl.touched,
        [`${formName}_untouched`]: () => formControl.untouched,
        [`${formName}_dirty`]: () => formControl.dirty,
        [`${formName}_pristine`]: () => formControl.pristine,
        [`${formName}_pending`]: () => formControl.pending,
        [`${formName}_disabled`]: () => formControl.disabled,
        [`${formName}_enabled`]: () => formControl.enabled,

        // Status tracking
        [`${formName}_status`]: () => formControl.status
      };

      // Register all the getters at once
      this.registerVariablesWithGetters(formGetters);
    });
  }

  /**
   * Update the original (baseline) values for comparison.
   * 
   * @method updateOriginalValues
   * @param {Object.<string, any>} variables - Object with key-value pairs to update
   * 
   * @description
   * This method allows you to update the baseline values against which changes are compared.
   * Useful when you want to establish a new "clean" state without re-registering variables.
   * 
   * @example
   * // Reset baseline after partial save
   * const detector = new ChangeDetector();
   * let user = { name: 'John', email: 'john@example.com', age: 30 };
   * 
   * detector.registerVariablesWithGetters({
   *   'user': () => user
   * });
   * 
   * user.name = 'Jane'; // Now dirty
   * console.log(detector.isDirty); // true
   * 
   * // Update only the name baseline
   * detector.updateOriginalValues({
   *   'user': { ...user, email: 'john@example.com' } // Keep email baseline unchanged
   * });
   */
  updateOriginalValues(variables: { [key: string]: any }): void {
    Object.entries(variables).forEach(([key, value]) => {
      if (this.trackedVariables.has(key)) {
        this.originalValues.set(key, this.deepClone(value));
      }
    });
  }

  /**
   * Check if any tracked variable has changed from its original value.
   * 
   * @readonly
   * @type {boolean}
   * @returns {boolean} True if any variable is dirty, false if all variables are clean
   * 
   * @description
   * This getter performs a deep comparison between original and current values for all
   * tracked variables. It returns true as soon as any difference is found, making it
   * efficient for large datasets.
   * 
   * @example
   * // Form dirty state checking
   * class FormComponent {
   *   private detector = new ChangeDetector();
   *   
   *   get canNavigateAway() {
   *     if (this.detector.isDirty) {
   *       return confirm('You have unsaved changes. Are you sure you want to leave?');
   *     }
   *     return true;
   *   }
   *   
   *   get saveButtonText() {
   *     return this.detector.isDirty ? 'Save Changes' : 'No Changes';
   *   }
   * }
   */
  get isDirty(): boolean {
    for (const [key, getter] of this.trackedVariables) {
      const originalValue = this.originalValues.get(key);
      const currentValue = getter();
      if (!this.deepEqual(originalValue, currentValue)) {
        return true;
      }
    }
    return false;
  }

  isVariableDirty(key: string): boolean {
    if (!this.trackedVariables.has(key)) {
      return false;
    }

    const originalValue = this.originalValues.get(key);
    const getter = this.trackedVariables.get(key);
    const currentValue = getter!();
    return !this.deepEqual(originalValue, currentValue);
  }

  getDirtyVariables(): string[] {
    const dirtyKeys: string[] = [];
    for (const [key, getter] of this.trackedVariables) {
      const originalValue = this.originalValues.get(key);
      const currentValue = getter();
      if (!this.deepEqual(originalValue, currentValue)) {
        dirtyKeys.push(key);
      }
    }
    return dirtyKeys;
  }

  getCurrentValue(key: string): any {
    const getter = this.trackedVariables.get(key);
    return getter ? getter() : undefined;
  }

  getOriginalValue(key: string): any {
    return this.originalValues.get(key);
  }

  markAsClean(): void {
    this.trackedVariables.forEach((getter, key) => {
      const currentValue = getter();
      this.originalValues.set(key, this.deepClone(currentValue));
    });
  }

  markVariableAsClean(key: string): void {
    if (this.trackedVariables.has(key)) {
      const getter = this.trackedVariables.get(key);
      const currentValue = getter!();
      this.originalValues.set(key, this.deepClone(currentValue));
    }
  }

  unregisterVariable(key: string): void {
    this.originalValues.delete(key);
    this.trackedVariables.delete(key);
  }

  /**
   * Clear all tracked variables and reset the detector.
   * 
   * @method clear
   * 
   * @description
   * Removes all tracked variables and clears internal state. After calling this method,
   * the detector will be in a clean state with no tracked variables. This is useful
   * for cleanup or when you want to start fresh with new variables.
   * 
   * @example
   * // Component cleanup
   * class MyComponent implements OnDestroy {
   *   private detector = new ChangeDetector();
   *   
   *   ngOnDestroy() {
   *     // Clean up to prevent memory leaks
   *     this.detector.clear();
   *   }
   * }
   */
  clear(): void {
    this.originalValues.clear();
    this.trackedVariables.clear();
  }

  getTrackedKeys(): string[] {
    return Array.from(this.trackedVariables.keys());
  }

  getTrackedCount(): number {
    return this.trackedVariables.size;
  }

  hasTrackedVariables(): boolean {
    return this.trackedVariables.size > 0;
  }

  /**
   * Deep clone an object/value to prevent reference sharing.
   * 
   * @private
   * @method deepClone
   * @param {any} obj - The object to clone
   * @returns {any} Deep cloned object
   * 
   * @description
   * Creates a deep copy of any JavaScript value including objects, arrays, Maps, Sets,
   * Dates, and primitives. This ensures that the original values stored for comparison
   * are completely independent of the current values, preventing reference-based issues
   * that could lead to incorrect change detection.
   * 
   * **Supported Data Types:**
   * - **Primitives**: string, number, boolean, null, undefined, symbol, bigint
   * - **Objects**: Plain objects with nested properties
   * - **Arrays**: Including multidimensional and sparse arrays
   * - **Dates**: Preserves exact timestamp
   * - **Maps**: Including nested Maps with any key/value types
   * - **Sets**: Including Sets containing complex objects
   * - **Nested Structures**: Any combination of the above types
   * 
   * **Algorithm Details:**
   * - Uses recursive descent for nested structures
   * - Maintains object structure and prototype chain for plain objects
   * - Handles circular references by avoiding infinite recursion
   * - Preserves JavaScript built-in object types and their methods
   * - Memory efficient - creates new objects only when necessary
   * 
   * @example
   * // Internal usage examples - showing what gets cloned
   * 
   * // Primitive values (returned as-is)
   * deepClone(42) // → 42
   * deepClone('hello') // → 'hello'
   * deepClone(null) // → null
   * deepClone(undefined) // → undefined
   * deepClone(true) // → true
   * 
   * // Date objects (new instance with same timestamp)
   * const originalDate = new Date('2023-12-25');
   * const clonedDate = deepClone(originalDate);
   * console.log(originalDate === clonedDate); // false (different instances)
   * console.log(originalDate.getTime() === clonedDate.getTime()); // true (same timestamp)
   * 
   * // Arrays (recursive cloning of elements)
   * const originalArray = [1, { name: 'John' }, [2, 3]];
   * const clonedArray = deepClone(originalArray);
   * originalArray[1].name = 'Jane'; // Modify original
   * console.log(clonedArray[1].name); // Still 'John' (independent copy)
   * 
   * // Complex nested objects
   * const complexObj = {
   *   id: 1,
   *   user: {
   *     profile: {
   *       name: 'John',
   *       settings: {
   *         theme: 'dark',
   *         notifications: true
   *       }
   *     },
   *     preferences: ['coding', 'reading']
   *   },
   *   metadata: {
   *     created: new Date(),
   *     tags: new Set(['important', 'user-data']),
   *     cache: new Map([['key1', 'value1'], ['key2', { nested: true }]])
   *   }
   * };
   * 
   * const clonedObj = deepClone(complexObj);
   * // Any modifications to originalObj won't affect clonedObj
   */
  private deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }

    if (obj instanceof Array) {
      return obj.map(item => this.deepClone(item));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(item => this.deepClone(item)));
    }

    if (obj instanceof Map) {
      const clonedMap = new Map();
      obj.forEach((value, key) => {
        clonedMap.set(this.deepClone(key), this.deepClone(value));
      });
      return clonedMap;
    }

    if (typeof obj === 'object') {
      const clonedObj: any = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = this.deepClone(obj[key]);
      });
      return clonedObj;
    }

    return obj;
  }

  /**
   * Deep comparison of two values for equality.
   * 
   * @private
   * @method deepEqual
   * @param {any} a - First value to compare
   * @param {any} b - Second value to compare
   * @returns {boolean} True if values are deeply equal, false otherwise
   * 
   * @description
   * Performs a deep structural equality comparison between two values. This method handles all
   * JavaScript data types and compares them by value rather than by reference. It's the core
   * algorithm used to determine if a variable has changed from its original state. The comparison
   * is thorough, recursive, and handles complex nested data structures while maintaining performance.
   * 
   * **Comparison Rules by Type:**
   * 
   * **Primitives (string, number, boolean, null, undefined):**
   * - Compared using strict equality (===)
   * - null and undefined are handled specially for consistency
   * - NaN is considered equal to NaN (unlike standard === comparison)
   * 
   * **Objects (Plain objects):**
   * - Compared recursively by all enumerable properties
   * - Property order doesn't matter: {a:1, b:2} equals {b:2, a:1}
   * - Must have exactly the same properties (no extra properties allowed)
   * - Nested objects are compared recursively
   * 
   * **Arrays:**
   * - Compared by length first (fast rejection)
   * - Each element compared recursively at same index
   * - Order matters: [1,2,3] is not equal to [3,2,1]
   * - Handles sparse arrays (arrays with undefined gaps)
   * - Multidimensional arrays compared recursively
   * 
   * **Dates:**
   * - Compared by timestamp using getTime()
   * - Two Date objects are equal if they represent the same moment
   * - Timezone information is normalized through timestamp comparison
   * 
   * **Maps:**
   * - Compared by size first (fast rejection)
   * - Each key-value pair must exist in both Maps
   * - Keys and values are compared using deep equality
   * - Key order doesn't matter (Maps maintain insertion order but equality ignores it)
   * 
   * **Sets:**
   * - Compared by size first (fast rejection)  
   * - Converted to sorted arrays for order-independent comparison
   * - Each element compared using deep equality
   * - Set([1,2,3]) equals Set([3,1,2]) - order independent
   * 
   */
  private deepEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return a === b;
    }

    if (typeof a !== typeof b) {
      return false;
    }

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (a instanceof Array && b instanceof Array) {
      if (a.length !== b.length) {
        return false;
      }
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }

    if (a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) {
        return false;
      }
      const arrA = Array.from(a).sort();
      const arrB = Array.from(b).sort();
      return this.deepEqual(arrA, arrB);
    }

    if (a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) {
        return false;
      }
      for (const [key, value] of a) {
        if (!b.has(key) || !this.deepEqual(value, b.get(key))) {
          return false;
        }
      }
      return true;
    }

    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) {
        return false;
      }

      for (const key of keysA) {
        if (!keysB.includes(key) || !this.deepEqual(a[key], b[key])) {
          return false;
        }
      }

      return true;
    }

    return false;
  }
}